import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Dropdown, Typography, Space } from 'antd';
import {
  UserOutlined,
  LogoutOutlined,
  MenuOutlined,
  CloseOutlined,
  CloudOutlined,
  SafetyOutlined,
  RocketOutlined,
  FileTextOutlined,
  MailOutlined,
} from '@ant-design/icons';

const { Text } = Typography;
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const Header = ({ user, onLogout, isLoading }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currency, setCurrency] = useState(() => localStorage.getItem('currency') || 'EUR');
  const closeMobileMenu = () => setMobileMenuOpen(false);
  const navItems = [
    { to: '/#solutions', label: 'Solutions' },
    { to: '/#pricing', label: 'Pricing' },
    { to: '/docs', label: 'Docs' },
    { to: '/contact', label: 'Contact' },
  ];

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    localStorage.setItem('currency', currency);
    window.dispatchEvent(new CustomEvent('currency-change', { detail: { currency } }));
  }, [currency]);

  useEffect(() => {
    let isActive = true;
    const loadRates = async () => {
      try {
        const response = await fetch(`${API_URL}/api/rates?symbol=GBP`);
        const data = await response.json();
        if (!response.ok) throw new Error(data?.error || 'Failed to fetch rates');
        if (isActive) {
          localStorage.setItem('rate_gbp', String(data.rate));
          localStorage.setItem('rate_gbp_date', data.date);
          window.dispatchEvent(new CustomEvent('currency-rate-update', { detail: { rate: data.rate, date: data.date } }));
        }
      } catch (err) {
        // Silently ignore rate fetch errors for UI.
      }
    };
    loadRates();
    const interval = setInterval(loadRates, 6 * 60 * 60 * 1000);
    return () => {
      isActive = false;
      clearInterval(interval);
    };
  }, []);

  const userMenuItems = [
    {
      key: 'dashboard',
      label: <Link to="/dashboard" onClick={closeMobileMenu}><CloudOutlined /> Dashboard</Link>,
    },
    ...(user?.role === 'admin' ? [{
      key: 'admin',
      label: <Link to="/admin" onClick={closeMobileMenu}><SafetyOutlined /> Admin Panel</Link>,
    }] : []),
    { type: 'divider' },
    {
      key: 'logout',
      label: (
        <span onClick={() => { closeMobileMenu(); onLogout(); }}>
          <LogoutOutlined /> Logout
        </span>
      ),
    },
  ];

  return (
    <header className="main-header">
      <div className="header-container">
        <Link to="/" className="header-brand">
          <span className="header-brand-icon"><RocketOutlined /></span>
          <span>CLOUNIQ</span>
        </Link>

        <nav className={`header-nav ${mobileMenuOpen ? 'open' : ''}`}>
          {navItems.map((item) => (
            <Link key={item.to} to={item.to} className="nav-link" onClick={closeMobileMenu}>
              {item.label}
            </Link>
          ))}

          <div className="header-nav-mobile-actions">
            <div className="header-nav-currency" aria-label="Currency selector">
              {['EUR', 'GBP'].map((code) => (
                <button
                  key={code}
                  type="button"
                  onClick={() => setCurrency(code)}
                  className={`currency-toggle ${currency === code ? 'active' : ''}`}
                  aria-pressed={currency === code}
                >
                  {code}
                </button>
              ))}
            </div>

            {isLoading ? null : user ? (
              <>
                <Link to="/dashboard" className="nav-mobile-action" onClick={closeMobileMenu}>
                  <CloudOutlined /> Workspace
                </Link>
                {user?.role === 'admin' && (
                  <Link to="/admin" className="nav-mobile-action" onClick={closeMobileMenu}>
                    <SafetyOutlined /> Admin Panel
                  </Link>
                )}
                <button className="nav-mobile-action nav-mobile-logout" onClick={() => { closeMobileMenu(); onLogout(); }}>
                  <LogoutOutlined /> Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/docs" className="nav-mobile-action" onClick={closeMobileMenu}>
                  <FileTextOutlined /> Product docs
                </Link>
                <Link to="/contact" className="nav-mobile-action" onClick={closeMobileMenu}>
                  <MailOutlined /> Talk to us
                </Link>
                <Link to="/login" className="nav-mobile-action" onClick={closeMobileMenu}>Sign in</Link>
                <Link to="/register" className="nav-mobile-action nav-mobile-primary" onClick={closeMobileMenu}>Get started</Link>
              </>
            )}
          </div>
        </nav>

        <div className="header-actions">
          <div className="header-currency">
            <div className="currency-switcher">
              {['EUR', 'GBP'].map((code) => (
                <button
                  key={code}
                  type="button"
                  onClick={() => setCurrency(code)}
                  className={`currency-toggle ${currency === code ? 'active' : ''}`}
                  aria-pressed={currency === code}
                >
                  {code}
                </button>
              ))}
            </div>
          </div>

          {isLoading ? (
            <div className="header-actions-loading" />
          ) : user ? (
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" trigger={['click']} overlayClassName="header-user-dropdown">
              <div className="header-user">
                <div className="user-avatar-small">
                  {user.avatar ? (
                    <img src={user.avatar} alt={user.name} />
                  ) : (
                    <UserOutlined />
                  )}
                </div>
                <Text className="user-name-small">{user.name || user.email}</Text>
              </div>
            </Dropdown>
          ) : (
            <Space>
              <Link to="/login">
                <Button type="text" className="header-btn-text">Sign in</Button>
              </Link>
              <Link to="/register">
                <Button type="primary" className="header-btn-primary">Start now</Button>
              </Link>
            </Space>
          )}

          <button
            className="mobile-menu-btn"
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <CloseOutlined /> : <MenuOutlined />}
          </button>
        </div>
      </div>
      {mobileMenuOpen && <button className="header-menu-backdrop" aria-label="Close menu" onClick={closeMobileMenu} />}
    </header>
  );
};

export default Header;



